import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import useLenis from "@/hooks/useLenis";

/**
 * Scroll helper — prefers Lenis so the motion matches the rest of the site,
 * but only when Lenis is actually being driven. Lenis virtualises scrolling,
 * so an un-ticked instance silently swallows scrollTo; that happens whenever
 * rAF is throttled (background tab, prerender), and there we scroll natively.
 */
function scrollToTarget(el) {
    const lenis = window.__lenis;
    if (lenis?.__driven && lenis.scrollTo) {
        lenis.scrollTo(el, { offset: -8, duration: 1.25 });
        return;
    }
    el.scrollIntoView({ behavior: "auto", block: "start" });
}

/**
 * PageShell — chrome shared by every public page: fixed nav, footer,
 * back-to-top and smooth scroll. On navigation it jumps to the top, or to the
 * hash target when one is present (e.g. /#work arriving from another page).
 */
export default function PageShell({ children }) {
    useLenis();
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (!hash) {
            window.scrollTo(0, 0);
            return;
        }

        // The target section can mount a tick after the route does. Poll on a
        // timer rather than rAF: rAF is suspended while a tab is not
        // compositing, which would strand the jump entirely.
        let tries = 0;
        let timer;
        const seek = () => {
            const el = document.querySelector(hash);
            if (el) {
                scrollToTarget(el);
                return;
            }
            if (tries++ < 20) timer = setTimeout(seek, 50);
        };
        timer = setTimeout(seek, 50);
        return () => clearTimeout(timer);
    }, [pathname, hash]);

    return (
        <div className="bg-[#F6F3EC] text-[#1B1B1B] min-h-screen flex flex-col">
            <Nav />
            <main className="flex-1 pt-16 md:pt-20">{children}</main>
            <Footer />
            <BackToTop />
        </div>
    );
}
